/**
 * 大屏字体唯一数据源（侧栏下拉、各组件 fontFamily 键 → CSS）
 *
 * - 分组选项：FONT_FAMILY_OPTION_GROUPS（下拉用）
 * - 键 → font-family 字符串：FONT_FAMILY_CSS_MAP + getFontFamilyCss
 * - 自定义 woff2 见 @/styles/chart-fonts.scss；其余为跨平台回退栈
 *
 * 历史别名：TITLE_FONT_* / getTitleFontFamilyCss 仍导出，与新名称等价。
 */

const FONT_FAMILY_CSS_MAP = {
  lianmeng: '"ChartFontLianMeng","Microsoft YaHei","PingFang SC",sans-serif',
  zitiquan: '"ChartFontZiTiQuan","Microsoft YaHei","PingFang SC",sans-serif',
  systemUi: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI","Helvetica Neue",Helvetica,Arial,sans-serif',
  yahei: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Source Han Sans SC","Noto Sans CJK SC",sans-serif',
  pingfang: '"PingFang SC","Microsoft YaHei","Hiragino Sans GB","Helvetica Neue",Helvetica,Arial,sans-serif',
  songti: '"Songti SC",SimSun,"STSong","NSimSun",serif',
  heiti: '"Heiti SC","STHeiti","Microsoft YaHei",SimHei,"PingFang SC",sans-serif',
  kaiti: '"Kaiti SC",KaiTi,"STKaiti",serif',
  fangsong: '"FangSong","FangSong_GB2312","STFangsong",SimSun,serif',
  arial: 'Arial,"Helvetica Neue",Helvetica,sans-serif',
  helvetica: '"Helvetica Neue",Helvetica,Arial,sans-serif',
  segoe: '"Segoe UI","Helvetica Neue",Helvetica,Arial,sans-serif',
  times: '"Times New Roman",Times,"Songti SC",STSong,serif',
  georgia: 'Georgia,"Times New Roman",Times,serif',
  verdana: 'Verdana,Geneva,sans-serif',
  tahoma: 'Tahoma,Geneva,Verdana,sans-serif',
  trebuchet: '"Trebuchet MS","Lucida Grande","Lucida Sans Unicode",Arial,sans-serif',
  courier: '"Courier New",Courier,Monaco,Consolas,monospace',
  menlo: 'Menlo,Monaco,Consolas,"Courier New",monospace',
  impact: 'Impact,Haettenschweiler,"Arial Narrow Bold",sans-serif',
}

/** 侧栏字体下拉分组（唯一列表，全项目共用） */
export const FONT_FAMILY_OPTION_GROUPS = [
  {
    label: '默认',
    options: [{ label: '默认', value: '' }],
  },
  {
    label: '自定义打包字体',
    options: [
      { label: '脸萌体', value: 'lianmeng' },
      { label: '字体圈', value: 'zitiquan' },
    ],
  },
  {
    label: '系统与中文（跨平台回退）',
    options: [
      { label: '微软雅黑', value: 'yahei' },
      { label: '苹方优先', value: 'pingfang' },
      { label: '宋体', value: 'songti' },
      { label: '黑体', value: 'heiti' },
      { label: '楷体', value: 'kaiti' },
      { label: '仿宋', value: 'fangsong' },
    ],
  },
  {
    label: '西文无衬线',
    options: [
      { label: 'Arial', value: 'arial' },
      { label: 'Helvetica', value: 'helvetica' },
      { label: 'Segoe UI', value: 'segoe' },
      { label: 'Verdana', value: 'verdana' },
      { label: 'Tahoma', value: 'tahoma' },
      { label: 'Trebuchet MS', value: 'trebuchet' },
      { label: 'Impact', value: 'impact' },
    ],
  },
  {
    label: '西文衬线',
    options: [
      { label: 'Times New Roman', value: 'times' },
      { label: 'Georgia', value: 'georgia' },
    ],
  },
  {
    label: '等宽',
    options: [
      { label: 'Courier New', value: 'courier' },
      { label: 'Menlo / Monaco', value: 'menlo' },
    ],
  },
]

/** @deprecated 请使用 FONT_FAMILY_OPTION_GROUPS */
export const TITLE_FONT_OPTION_GROUPS = FONT_FAMILY_OPTION_GROUPS

/** 扁平列表（兼容旧引用） */
export const TITLE_FONT_OPTIONS = FONT_FAMILY_OPTION_GROUPS.flatMap((g) => g.options)

/** fontFamily 配置键 → CSS font-family */
export function getFontFamilyCss (key) {
  if (key == null || key === '') return undefined
  return FONT_FAMILY_CSS_MAP[key]
}

/** @deprecated 请使用 getFontFamilyCss */
export function getTitleFontFamilyCss (key) {
  return getFontFamilyCss(key)
}

/** 标题行：对齐、字号、颜色、字体（不含位移，可供外层容器使用） */
export function getTitleBarTypographyInlineStyle (titleStyle = {}) {
  const t = titleStyle || {}
  const style = {
    width: '100%',
    textAlign: t.align || 'left',
    fontSize: (t.fontSize != null ? t.fontSize : 14) + 'px',
    color: t.color,
  }
  const ff = getFontFamilyCss(t.fontFamily)
  if (ff) style.fontFamily = ff
  return style
}

/** 标题行：仅 titleOffsetX/Y 对应的位移样式（供文字层单独使用，避免带动整块/边框图） */
export function getTitleBarOffsetInlineStyle (titleStyle = {}) {
  const t = titleStyle || {}
  const ox = Number(t.titleOffsetX)
  const oy = Number(t.titleOffsetY)
  const dx = Number.isFinite(ox) ? ox : 0
  const dy = Number.isFinite(oy) ? oy : 0
  if (dx === 0 && dy === 0) return {}
  return {
    transform: `translate(${dx}px, ${dy}px)`,
    position: 'relative',
    zIndex: '1',
  }
}

/** 标题行内联样式（对齐、字号、颜色、可选字体、可选位移） — 与其它图表标题条行为一致 */
export function getTitleBarInlineStyle (titleStyle = {}) {
  return {
    ...getTitleBarTypographyInlineStyle(titleStyle),
    ...getTitleBarOffsetInlineStyle(titleStyle),
  }
}

/**
 * 标题边框模版图样式：拉伸时铺满容器；不拉伸时保持比例居中
 * borderTemplateStretch 未定义时视为 true（与历史行为一致）
 */
export function getBorderTemplateImageStyle (borderStyle = {}) {
  const b = borderStyle || {}
  const stretch = b.borderTemplateStretch !== false
  if (stretch) {
    return {
      display: 'inline-block',
      width: '100%',
      height: '100%',
    }
  }
  return {
    display: 'block',
    width: 'auto',
    maxWidth: '100%',
    height: 'auto',
    maxHeight: '100%',
    objectFit: 'contain',
    margin: '0 auto',
  }
}
